import { useMutation, useQuery } from "@tanstack/react-query";
import type { CreatorListTemplate } from "@unbox/api";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { type Pagination, toQuery } from "./pagination";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useCreatorLists = (
  pagination: Pagination,
  text: string,
  sort: { column: "createdAt" | "name"; direction: "asc" | "desc" },
) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: [
      "creator-lists",
      scope.merchantId,
      scope.workspaceId,
      pagination.limit,
      pagination.offset,
      text,
      sort.column,
      sort.direction,
    ],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-lists"].$get({
            param: scope.param,
            query: {
              ...toQuery(pagination),
              text: text === "" ? undefined : text,
              column: sort.column,
              direction: sort.direction,
            },
          })
        : await client.merchant[":merchantId"]["creator-lists"].$get({
            param: scope.param,
            query: {
              ...toQuery(pagination),
              text: text === "" ? undefined : text,
              column: sort.column,
              direction: sort.direction,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useCreatorList = (id: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["creator-lists", scope.merchantId, scope.workspaceId, id],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-lists"][":id"].$get({
            param: { ...scope.param, id },
          })
        : await client.merchant[":merchantId"]["creator-lists"][":id"].$get({
            param: { ...scope.param, id },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useCreateCreatorList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      description?: string;
      creatorIds?: string[];
      template?: CreatorListTemplate;
    }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-lists"].$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"]["creator-lists"].$post({
            param: scope.param,
            json: payload,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-lists"] });
    },
  });
};

export const useEditCreatorList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: { id: string; name: string; description?: string }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: payload,
          })
        : await client.merchant[":merchantId"]["creator-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: payload,
          });
      if ((res.status as number) === 204) return;
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-lists"] });
    },
  });
};

export const useDeleteCreatorList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: { id: string }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-lists"][":id"].$delete({
            param: { ...scope.param, id: payload.id },
          })
        : await client.merchant[":merchantId"]["creator-lists"][":id"].$delete({
            param: { ...scope.param, id: payload.id },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-lists"] });
    },
  });
};

export const useAddToCreatorList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: { id: string; creators: string[] }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: {
              addCreators: payload.creators,
            },
          })
        : await client.merchant[":merchantId"]["creator-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: {
              addCreators: payload.creators,
            },
          });
      if ((res.status as number) === 204) return;
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-lists"] });
      queryClient.invalidateQueries({ queryKey: ["creator-overview"] });
    },
  });
};

export const useRemoveFromCreatorList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: { id: string; creators: string[] }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: {
              removeCreators: payload.creators,
            },
          })
        : await client.merchant[":merchantId"]["creator-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: {
              removeCreators: payload.creators,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-lists"] });
      queryClient.invalidateQueries({ queryKey: ["creator-overview"] });
    },
  });
};
